/**
 * Shared i18n types and utilities
 *
 * Follows the pattern from crm-report-translations.ts:
 * Record-based translations + t() lookup + interpolate() helper.
 */

export type Language = 'en' | 'uk' | 'ru' | 'es';

export type Translations = Record<Language, Record<string, string>>;

/**
 * Get translation by key from a translations record
 *
 * @param lang - Language code (uk, ru, en, es). Falls back to 'en' if unknown.
 * @param translations - The translations record to look up
 * @param key - Translation key
 * @returns Translated string, or English fallback, or the key itself
 */
export function t(lang: string, translations: Translations, key: string): string {
  const language = (lang in translations ? lang : 'en') as Language;
  return translations[language][key] || translations.en[key] || key;
}

/**
 * Interpolate variables into a template string
 *
 * @param template - Template with {{variable}} placeholders
 * @param vars - Key-value pairs to substitute
 * @returns Interpolated string
 *
 * @example
 * interpolate('Hello {{name}}, welcome to {{org}}!', { name: 'John', org: 'AICA' })
 * // => 'Hello John, welcome to AICA!'
 */
export function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || '');
}
